import { PropsWithChildren, useEffect } from 'react'
import { useOptimizelyFlagContext } from '@/scopes/common/infrastructure/ab-testing/optimizely/useOptimizely'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { useFeatureFlag } from '@/scopes/common/infrastructure/feature-flags'
import { BannerCampaigns } from '@/scopes/prices/components/Banner'
import Banner from '@/scopes/prices/components/Banner/Banner'
import { useDateCountdown } from '@/scopes/prices/domain/promoted-price/hooks'
import { PromotedPricePrice } from '@/scopes/prices/domain/promoted-price/types'

interface UrgencyCampaignProps extends PropsWithChildren {
  price?: PromotedPricePrice
  campaignId?: string
  campaignCountdown?: string
  campaignCountdownText?: string
}

export default function UrgencyCampaign({ price, campaignId, campaignCountdown, children, campaignCountdownText }: UrgencyCampaignProps) {
  const showUrgencyBanner = (price?.discountPercentage || 0) > 0 || campaignId

  if (!showUrgencyBanner && !campaignCountdown) {
    return null
  }

  const variant: BannerCampaigns
    = campaignCountdown && campaignCountdownText
      ? BannerCampaigns.Countdown
      : BannerCampaigns.Urgency

  if (variant === BannerCampaigns.Urgency) {
    return <Banner />
  }

  if (variant === BannerCampaigns.Countdown && campaignCountdown) {
    return <CampaignCountdown campaignCountDown={campaignCountdown}>{children}</CampaignCountdown>
  }
}

function CampaignCountdown({ children, campaignCountDown }: PropsWithChildren<{ campaignCountDown: string }>) {
  const { showBlackFridayCountdown: showBlackFridayCountdownOptimizelyDecision } = useOptimizelyFlagContext()
  const shouldShowCountdownFeatureFlag = useFeatureFlag('BAHAMAS_ENABLE_COUNTDOWNS')
  const { sendEvent } = useAnalytics()
  const { timeToEnd, counterEnded } = useDateCountdown(campaignCountDown)

  const showCountdown = shouldShowCountdownFeatureFlag || showBlackFridayCountdownOptimizelyDecision

  useEffect(() => {
    if (!shouldShowCountdownFeatureFlag) {
      sendEvent({
        event: 'CRO',
        description: 'ID 445 | Blackfriday countdown on promoted price',
        element: 'NL',
        type: 'A/B test',
        result: showBlackFridayCountdownOptimizelyDecision ? 'Variant' : 'Default',
        interaction: false,
      })
    }
  }, [shouldShowCountdownFeatureFlag, showBlackFridayCountdownOptimizelyDecision])

  if (counterEnded || !timeToEnd || !showCountdown) {
    return <Banner />
  }

  return <Banner campaign={BannerCampaigns.Countdown} endDate={timeToEnd}>{children}</Banner>
}
